import {
  Box,
  Flex,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Link,
  Select,
  Stack,
  Wrap,
} from "@chakra-ui/react";
import FormController from "@raiden/library/components/ReactHookForm/FormController";
import {
  POSTS_SORT_DATE_LIST,
  POSTS_TYPE_VALUE_EVENT,
} from "@raiden/library/constants/posts";
import { useSearchInfiniteContext } from "@raiden/library/contexts/SearchInfinite/useSearchInfiniteContext";
import useTranslate from "@raiden/library/hooks/useTranslate";
import capitalize from "@splitfire-agency/raiden-library/dist/libraries/utils/capitalize";
import { useMemo } from "react";
import { MdOutlineCalendarToday, MdSearch } from "react-icons/md";
import { FormattedMessage, useIntl } from "react-intl";
import { DataHandler } from "../../../../components/DataHandler";
import { TabInput } from "../../../../components/Form/TabInput";

/**
 * @typedef {Object} Props
 * @property {import("swr").SWRResponse<import("@raiden/library/types/Api/ApiResponse").ApiResponse<import("@raiden/library/types/PostCategory").PostCategory[]>>} swrResponsePostCategories
 * @property {import("@raiden/library/types/Post").PostType} postType
 *
 * @param {Props} props
 */
export function Filters({ swrResponsePostCategories, postType }) {
  const intl = useIntl();

  const translate = useTranslate();

  /** @type {import("@raiden/library/contexts/SearchInfinite/useSearchInfiniteContext").UseSearchInfiniteReturn<import(".").PostsSearchFormValues>} */
  const {
    form: { control },
    appliedFilters,
    reset,
  } = useSearchInfiniteContext();

  const tabInputItems = useMemo(() => {
    const categories = swrResponsePostCategories.data?.data ?? [];
    /** @type {import("../../../../components/Form/TabInput").TabInputItem<any>[]} */
    const items = [
      {
        label: intl.formatMessage({
          defaultMessage: "Tout",
        }),
        value: [],
      },
    ];
    categories.forEach((category) => {
      items.push({
        label: translate(category.name) ?? String(category.id),
        value: [String(category.id)],
      });
    });
    return items;
  }, [intl, swrResponsePostCategories.data?.data, translate]);

  return (
    <Stack spacing="0.375rem">
      <Box>
        <Stack direction={{ base: "column", md: "row" }}>
          <FormController
            control={control}
            name="term"
            flexGrow={1}
            render={(field) => (
              <InputGroup>
                <Input
                  type="search"
                  placeholder={intl.formatMessage({
                    defaultMessage: "Rechercher...",
                  })}
                  w="full"
                  {...field}
                />

                <InputLeftElement zIndex="initial">
                  <Icon as={MdSearch} />
                </InputLeftElement>
              </InputGroup>
            )}
          />

          {postType === POSTS_TYPE_VALUE_EVENT && (
            <Wrap minW="max-content">
              <FormController
                control={control}
                name="event_date"
                render={(field) => (
                  <InputGroup mr="2rem">
                    <InputLeftElement zIndex="initial">
                      <Icon
                        as={MdOutlineCalendarToday}
                        fontSize="1.5rem"
                        color="gray.400"
                      />
                    </InputLeftElement>

                    <Input as={Select} {...field} defaultValue="">
                      {POSTS_SORT_DATE_LIST.map((type) => (
                        <option value={type.id} key={type.id}>
                          {capitalize(intl.formatMessage(type.label))}
                        </option>
                      ))}

                      <option value={""}>
                        {intl.formatMessage({ defaultMessage: "Tout" })}
                      </option>
                    </Input>
                  </InputGroup>
                )}
              />
            </Wrap>
          )}
        </Stack>

        <Flex
          justify="flex-end"
          pt=".25rem"
          opacity={appliedFilters.length > 0 ? 1 : 0}
          visibility={appliedFilters.length > 0 ? "visible" : "hidden"}
          transition="all .25s ease">
          <Link
            onClick={reset}
            textAlign="right"
            fontSize="0.75rem"
            color="gray.600">
            <FormattedMessage defaultMessage="Réinitialiser les filtres" />
          </Link>
        </Flex>
      </Box>

      <DataHandler
        swrResponse={swrResponsePostCategories}
        renderNoResultSplash={false}
        keepDataOnRevalidation>
        {() => (
          <FormController
            control={control}
            name="category_id"
            render={(field) => <TabInput items={tabInputItems} {...field} />}
          />
        )}
      </DataHandler>
    </Stack>
  );
}
